import { create } from 'zustand';
import type { GenerationConstraints, RangeConstraint } from '@/generator/constraints';
import { DEFAULT_LOT_WIDTH, DEFAULT_LOT_DEPTH } from '@/generator/constraints';
import type { RoofType, StylePreset } from '@/model/schema';

/**
 * User-controlled generation inputs. Separated from `configStore` so that
 * editing a slider doesn't implicitly mutate the canonical output — the user
 * has to press Generate (or the app auto-regens on debounced change).
 */
export interface ConstraintsState {
  seed: string;
  stylePreset: { value: StylePreset; locked: boolean };
  storyCount: { value: number; locked: boolean };
  lotWidth: RangeConstraint;
  lotDepth: RangeConstraint;
  roofType: { value: RoofType; locked: boolean };
  fence: { value: boolean; locked: boolean };
  mailbox: { value: boolean; locked: boolean };
  bedrooms: { value: number; locked: boolean };
  bathrooms: { value: number; locked: boolean };
  symmetricFront: { value: boolean; locked: boolean };
  basement: { value: boolean; locked: boolean };
  garage: { value: boolean; locked: boolean };

  setSeed(seed: string): void;
  setStylePreset(value: StylePreset, locked?: boolean): void;
  setStoryCount(value: number, locked?: boolean): void;
  setLotWidth(r: Partial<RangeConstraint>): void;
  setLotDepth(r: Partial<RangeConstraint>): void;
  setRoofType(value: RoofType, locked?: boolean): void;
  setFence(value: boolean, locked?: boolean): void;
  setMailbox(value: boolean, locked?: boolean): void;
  setBedrooms(value: number, locked?: boolean): void;
  setBathrooms(value: number, locked?: boolean): void;
  setSymmetricFront(value: boolean, locked?: boolean): void;
  setBasement(value: boolean, locked?: boolean): void;
  setGarage(value: boolean, locked?: boolean): void;
  toggleLock(field: LockableField): void;

  toGenerationInput(): GenerationConstraints;
}

export type LockableField =
  | 'stylePreset'
  | 'storyCount'
  | 'lotWidth'
  | 'lotDepth'
  | 'roofType'
  | 'fence'
  | 'mailbox'
  | 'bedrooms'
  | 'bathrooms'
  | 'symmetricFront'
  | 'basement'
  | 'garage';

export const useConstraintsStore = create<ConstraintsState>((set, get) => ({
  seed: 'structure',
  stylePreset: { value: 'suburban', locked: false },
  storyCount: { value: 2, locked: false },
  lotWidth: { ...DEFAULT_LOT_WIDTH },
  lotDepth: { ...DEFAULT_LOT_DEPTH },
  roofType: { value: 'gable', locked: false },
  fence: { value: false, locked: false },
  mailbox: { value: true, locked: false },
  bedrooms: { value: 2, locked: false },
  bathrooms: { value: 1, locked: false },
  symmetricFront: { value: false, locked: false },
  basement: { value: false, locked: false },
  garage: { value: false, locked: false },

  setSeed: (seed) => set({ seed }),
  setStylePreset: (value, locked) =>
    set((s) => ({ stylePreset: { value, locked: locked ?? s.stylePreset.locked } })),
  setStoryCount: (value, locked) =>
    set((s) => ({ storyCount: { value, locked: locked ?? s.storyCount.locked } })),
  setLotWidth: (r) => set((s) => ({ lotWidth: { ...s.lotWidth, ...r } })),
  setLotDepth: (r) => set((s) => ({ lotDepth: { ...s.lotDepth, ...r } })),
  setRoofType: (value, locked) =>
    set((s) => ({ roofType: { value, locked: locked ?? s.roofType.locked } })),
  setFence: (value, locked) =>
    set((s) => ({ fence: { value, locked: locked ?? s.fence.locked } })),
  setMailbox: (value, locked) =>
    set((s) => ({ mailbox: { value, locked: locked ?? s.mailbox.locked } })),
  setBedrooms: (value, locked) =>
    set((s) => ({ bedrooms: { value, locked: locked ?? s.bedrooms.locked } })),
  setBathrooms: (value, locked) =>
    set((s) => ({ bathrooms: { value, locked: locked ?? s.bathrooms.locked } })),
  setSymmetricFront: (value, locked) =>
    set((s) => ({ symmetricFront: { value, locked: locked ?? s.symmetricFront.locked } })),
  setBasement: (value, locked) =>
    set((s) => ({ basement: { value, locked: locked ?? s.basement.locked } })),
  setGarage: (value, locked) =>
    set((s) => ({ garage: { value, locked: locked ?? s.garage.locked } })),

  toggleLock: (field) =>
    set((s) => {
      const val = s[field] as { locked: boolean };
      return { [field]: { ...val, locked: !val.locked } } as Partial<ConstraintsState>;
    }),

  toGenerationInput: () => {
    const s = get();
    return {
      seed: s.seed,
      stylePreset: { value: s.stylePreset.value, locked: s.stylePreset.locked },
      storyCount: { value: s.storyCount.value, locked: s.storyCount.locked },
      lotWidth: s.lotWidth,
      lotDepth: s.lotDepth,
      roofType: { value: s.roofType.value, locked: s.roofType.locked },
      fence: { value: s.fence.value, locked: s.fence.locked },
      mailbox: { value: s.mailbox.value, locked: s.mailbox.locked },
      bedrooms: { value: s.bedrooms.value, locked: s.bedrooms.locked },
      bathrooms: { value: s.bathrooms.value, locked: s.bathrooms.locked },
      symmetricFront: { value: s.symmetricFront.value, locked: s.symmetricFront.locked },
      basement: { value: s.basement.value, locked: s.basement.locked },
      garage: { value: s.garage.value, locked: s.garage.locked },
    };
  },
}));
